import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { DATE_FORMATS_FNS, NavPortal, useSelectedDealer } from "components";
import { useDealerItemsQuery } from "modules/DealerLicenseOverview/hooks";
import { calculateTotalCosts, getNextMonthlyInvoicingDate } from "modules/LocationLicenseOverview/utils";
import { formatDate } from "utils/dateHelperFunctions";
import { formatPrice } from "utils/formatPrice";

import type { Licenses } from "modules/LocationLicenseOverview/components";

type DealerLicensesNavbarProps = {
  licenses?: Licenses;
};

export const DealerLicenseNavbar = ({ licenses }: DealerLicensesNavbarProps) => {
  const { t } = useTranslation();

  const { dealerItems } = useDealerItemsQuery();
  const { selectedDealer } = useSelectedDealer();

  const isDealerLevelInvoicingEnabled = Boolean(selectedDealer?.is_exact_invoicing_enabled && selectedDealer.exact_account_id);

  const { totalOneTimeCosts, totalMonthlyCosts } = useMemo(
    () => ({
      totalOneTimeCosts: calculateTotalCosts(dealerItems, licenses?.oneTimeCategories, licenses?.oneTimeStandalones),
      totalMonthlyCosts: calculateTotalCosts(dealerItems, licenses?.monthlyCategories, licenses?.monthlyStandalones)
    }),
    [licenses, dealerItems]
  );

  return (
    <NavPortal>
      <div className="LocationLicenseOverview-Navbar">
        <div className="LocationLicenseOverview-Navbar-Item">
          <span>{t("v8_total_monthly", "Total monthly")}:</span>
          {`€ ${formatPrice(isDealerLevelInvoicingEnabled ? totalMonthlyCosts : null)}`}
        </div>
        <div className="LocationLicenseOverview-Navbar-Item">
          <span>{t("v8_total_once", "Total once")}:</span>
          {`€ ${formatPrice(isDealerLevelInvoicingEnabled ? totalOneTimeCosts : null)}`}
        </div>
        <div className="LocationLicenseOverview-Navbar-Item">
          <span>{t("v8_total_this_month", "Total this month")}:</span>
          {`€ ${formatPrice(isDealerLevelInvoicingEnabled ? totalMonthlyCosts + totalOneTimeCosts : null)}`}
        </div>
        <div className="LocationLicenseOverview-Navbar-Item">
          <span>{t("v8_next_monthly_bill", "Next monthly bill")}:</span>
          {isDealerLevelInvoicingEnabled ? formatDate(getNextMonthlyInvoicingDate(), DATE_FORMATS_FNS.dateMonthYear) : ""}
        </div>
      </div>
    </NavPortal>
  );
};
